import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShieldAlert, KeyRound, FileText, Users2, UserCog, Archive } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/StatusBadge";
import { ReportsTab } from "@/components/committee/ReportsTab";
import { MembersTab } from "@/components/committee/MembersTab";
import { AccessPanel } from "@/components/committee/AccessPanel";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 flex items-center gap-3">
    <Icon className="w-5 h-5 text-amber-600 shrink-0" />
    <div className="min-w-0"><div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="font-bold text-slate-900 truncate" data-testid={testid}>{value}</div></div>
  </div>
);

export default function CommitteeRoom() {
  const { id } = useParams();
  const { user, meta } = useAuth();
  const [c, setC] = useState(null);
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [access, setAccess] = useState(false);
  const loadInfo = useCallback(() => api.get(`/committees/${id}/office`).then((r) => setInfo(r.data)), [id]);
  useEffect(() => {
    setC(null); setInfo(null); setErr("");
    api.get(`/committees/${id}`).then((r) => setC(r.data)).catch((e) => setErr(errMsg(e)));
    loadInfo().catch(() => {});
  }, [id, loadInfo]);
  if (err) return <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-rose-700 flex gap-2 items-center" data-testid="committee-error"><ShieldAlert className="w-5 h-5" />{err}</div>;
  if (!c || !info) return <div className="h-40 animate-pulse rounded-lg bg-slate-100" />;
  const office = meta.offices.find((o) => o.id === id);
  return (
    <div>
      <PageHeader eyebrow={`Committee room${office?.role ? ` · ${office.role}` : ""}`} title={c.name} testid="committee-room-title">
        {info.is_super_admin && info.legacy_records > 0 && <Button variant="outline" asChild data-testid="committee-legacy-files"><Link to={`/records?office=${id}`}><Archive className="w-4 h-4 mr-1" />Earlier files ({info.legacy_records})</Link></Button>}
        {info.can_manage && <Button variant="outline" onClick={() => setAccess(true)} data-testid="committee-access-button"><KeyRound className="w-4 h-4 mr-1" />Access</Button>}
      </PageHeader>
      {c.description && <p className="text-sm text-slate-600 -mt-3 mb-5">{c.description}</p>}
      {!c.active && <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">This room is inactive — only the Super Admin can see it.</div>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Stat icon={FileText} label="Reports" value={info.report_count} testid="committee-report-count" />
        <Stat icon={Users2} label="Active members" value={info.active_members} testid="committee-active-members" />
        <Stat icon={UserCog} label="Committee Admin" value={info.admins.join(", ") || "Not assigned"} testid="committee-admins" />
      </div>
      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "reports"}>
        <TabsList className="bg-slate-100 mb-4 w-full sm:w-auto grid grid-cols-2 sm:inline-flex h-11">
          <TabsTrigger value="reports" className="h-9" data-testid="committee-tab-reports">Reports ({info.report_count})</TabsTrigger>
          <TabsTrigger value="members" className="h-9" data-testid="committee-tab-members">Members ({info.active_members})</TabsTrigger>
        </TabsList>
        <TabsContent value="reports"><ReportsTab cid={id} info={info} onChange={loadInfo} /></TabsContent>
        <TabsContent value="members"><MembersTab cid={id} info={info} name={c.name} onChange={loadInfo} /></TabsContent>
      </Tabs>
      {info.can_manage && <AccessPanel open={access} onClose={() => setAccess(false)} c={c} me={user.user_id} roles={meta.committee_roles} />}
    </div>
  );
}
